/**
 * Payment Schedule Form Utilities
 * 
 * Provides serializable form model and converters for payment schedules.
 * The form model uses plain JavaScript types (numbers, Date) instead of
 * protobuf types (BigInt, Timestamp) to avoid serialization issues with
 * TanStack Form.
 */

import { create } from '@bufbuild/protobuf'
import type { QuotePaymentScheduleInput, QuotePaymentSchedule, PaymentSchedule } from '@/gen/cortado/v1/invoices_pb'
import { QuotePaymentScheduleInputSchema } from '@/gen/cortado/v1/invoices_pb'
import type { CreatePaymentScheduleInput } from '@/gen/cortado/v1/cortado_pb'
import { CreatePaymentScheduleInputSchema } from '@/gen/cortado/v1/cortado_pb'
import {
  PaymentScheduleStatusEnum,
  QuotePaymentScheduleAmountTypeEnum,
  QuotePaymentScheduleDueDateAnchorEnum,
} from '@/gen/cortado/v1/settings_pb'
import { dateToDateOnlyTimestamp, timestampToDateOnly } from './date-utils'
import {
  normalizeDepositDueDays,
  normalizeDueDateAnchor,
  normalizeRemainderDueDays,
  resolveBeforeEventPreviewDueDate,
} from './quote-payment-schedule-defaults'

export const ADDITIONAL_CHARGES_PAYMENT_SCHEDULE_DESCRIPTION = 'Additional charges'

/**
 * Serializable form model for payment schedules
 * Uses plain JavaScript types that can be safely serialized by JSON.stringify
 */
export interface PaymentScheduleFormValue {
  sequenceNumber: number
  description: string
  dueDate: Date
  dueDateAnchor?: QuotePaymentScheduleDueDateAnchorEnum
  dueOffsetDays?: number
  amountType: QuotePaymentScheduleAmountTypeEnum
  percentage?: number
  amountCents?: number // Stored as number (not BigInt) for serialization
}

/**
 * Convert a protobuf QuotePaymentScheduleInput to a serializable form value
 */
export function paymentScheduleToFormValue(
  proto: QuotePaymentScheduleInput
): PaymentScheduleFormValue {
  const dueDate = timestampToDateOnly(proto.dueDate) ?? new Date()

  return {
    sequenceNumber: proto.sequenceNumber,
    description: proto.description,
    dueDate,
    dueDateAnchor: normalizeDueDateAnchor(proto.dueDateAnchor),
    dueOffsetDays: proto.dueOffsetDays ?? 0,
    amountType: proto.amountType,
    percentage: proto.percentage,
    amountCents: proto.amountCents ? Number(proto.amountCents) : undefined,
  }
}

/**
 * Convert a serializable form value to a protobuf QuotePaymentScheduleInput
 */
export function formValueToPaymentSchedule(
  formValue: PaymentScheduleFormValue
): QuotePaymentScheduleInput {
  return create(QuotePaymentScheduleInputSchema, {
    sequenceNumber: formValue.sequenceNumber,
    description: formValue.description,
    dueDate: dateToDateOnlyTimestamp(formValue.dueDate),
    dueDateAnchor: normalizeDueDateAnchor(formValue.dueDateAnchor),
    dueOffsetDays: formValue.dueOffsetDays ?? 0,
    amountType: formValue.amountType,
    percentage: formValue.percentage,
    amountCents: formValue.amountCents ? BigInt(formValue.amountCents) : undefined,
  })
}

/**
 * Convert a QuotePaymentSchedule (from API response) to a form value
 */
export function quotePaymentScheduleToFormValue(
  schedule: QuotePaymentSchedule
): PaymentScheduleFormValue {
  const dueDate = timestampToDateOnly(schedule.dueDate) ?? new Date()

  return {
    sequenceNumber: schedule.sequenceNumber,
    description: schedule.description,
    dueDate,
    dueDateAnchor: normalizeDueDateAnchor(schedule.dueDateAnchor),
    dueOffsetDays: schedule.dueOffsetDays ?? 0,
    amountType: schedule.amountType,
    percentage: schedule.percentage,
    amountCents: schedule.amountCents ? Number(schedule.amountCents) : undefined,
  }
}

/**
 * Convert an array of protobuf schedules to form values
 * Handles both QuotePaymentScheduleInput[] (for updates) and QuotePaymentSchedule[] (for initial load)
 */
export function paymentSchedulesToFormValues(
  schedules: QuotePaymentScheduleInput[] | QuotePaymentSchedule[]
): PaymentScheduleFormValue[] {
  return schedules.map((schedule) => {
    // Both types have the same structure for the fields we need, so we can use the same conversion
    const dueDate = timestampToDateOnly(schedule.dueDate) ?? new Date()

    return {
      sequenceNumber: schedule.sequenceNumber,
      description: schedule.description,
      dueDate,
      dueDateAnchor: normalizeDueDateAnchor(schedule.dueDateAnchor),
      dueOffsetDays: schedule.dueOffsetDays ?? 0,
      amountType: schedule.amountType,
      percentage: schedule.percentage,
      amountCents: schedule.amountCents ? Number(schedule.amountCents) : undefined,
    }
  })
}

/**
 * Convert an array of form values to protobuf schedules
 */
export function formValuesToPaymentSchedules(
  formValues: PaymentScheduleFormValue[]
): QuotePaymentScheduleInput[] {
  return formValues.map(formValueToPaymentSchedule)
}

/**
 * Build the payment-schedule rows for a quick template.
 *
 * All multi-payment templates use PERCENTAGE amounts so they automatically
 * rescale when the quote total changes. A "Deposit + Remainder" previously
 * stored FIXED cents frozen at selection time, which then failed validation on
 * any later total change and blocked quote edits (see inquiry 8cf5731d). Fixed
 * amounts remain available only via the explicit per-row amount-type toggle.
 */
export interface QuotePaymentScheduleTemplateOptions {
  now?: Date
  depositDays?: number
  remainderDays?: number
  eventDates?: Array<Date | undefined | null>
}

export function buildQuotePaymentScheduleTemplate(
  template: string,
  options: QuotePaymentScheduleTemplateOptions = {},
): PaymentScheduleFormValue[] {
  const now = options.now ?? new Date()
  const depositDays = normalizeDepositDueDays(options.depositDays)
  const remainderDays = normalizeRemainderDueDays(options.remainderDays)
  const remainderDueDate = resolveBeforeEventPreviewDueDate(now, remainderDays, options.eventDates ?? [])

  switch (template) {
    case 'full-upfront':
      return [
        {
          sequenceNumber: 1,
          description: 'Full Payment',
          dueDate: now,
          dueDateAnchor: QuotePaymentScheduleDueDateAnchorEnum.QUOTE_PAYMENT_SCHEDULE_DUE_DATE_ANCHOR_AFTER_ACCEPTANCE,
          dueOffsetDays: 0,
          amountType: QuotePaymentScheduleAmountTypeEnum.QUOTE_PAYMENT_SCHEDULE_AMOUNT_TYPE_PERCENTAGE,
          percentage: 100.0,
        },
      ]
    case '50-50':
      return [
        {
          sequenceNumber: 1,
          description: 'Deposit (50%)',
          dueDate: now,
          dueDateAnchor: QuotePaymentScheduleDueDateAnchorEnum.QUOTE_PAYMENT_SCHEDULE_DUE_DATE_ANCHOR_AFTER_ACCEPTANCE,
          dueOffsetDays: depositDays,
          amountType: QuotePaymentScheduleAmountTypeEnum.QUOTE_PAYMENT_SCHEDULE_AMOUNT_TYPE_PERCENTAGE,
          percentage: 50.0,
        },
        {
          sequenceNumber: 2,
          description: 'Final Payment (50%)',
          dueDate: remainderDueDate,
          dueDateAnchor: QuotePaymentScheduleDueDateAnchorEnum.QUOTE_PAYMENT_SCHEDULE_DUE_DATE_ANCHOR_BEFORE_EVENT,
          dueOffsetDays: remainderDays,
          amountType: QuotePaymentScheduleAmountTypeEnum.QUOTE_PAYMENT_SCHEDULE_AMOUNT_TYPE_PERCENTAGE,
          percentage: 50.0,
        },
      ]
    case 'deposit-remainder':
      // A 50/50 deposit/remainder split, expressed as PERCENTAGE so both rows
      // rescale with the quote total. Previously stored as FIXED cents frozen at
      // selection time, which blocked later quote edits (inquiry 8cf5731d).
      return [
        {
          sequenceNumber: 1,
          description: 'Deposit',
          dueDate: now,
          dueDateAnchor: QuotePaymentScheduleDueDateAnchorEnum.QUOTE_PAYMENT_SCHEDULE_DUE_DATE_ANCHOR_AFTER_ACCEPTANCE,
          dueOffsetDays: depositDays,
          amountType: QuotePaymentScheduleAmountTypeEnum.QUOTE_PAYMENT_SCHEDULE_AMOUNT_TYPE_PERCENTAGE,
          percentage: 50.0,
        },
        {
          sequenceNumber: 2,
          description: 'Remainder',
          dueDate: remainderDueDate,
          dueDateAnchor: QuotePaymentScheduleDueDateAnchorEnum.QUOTE_PAYMENT_SCHEDULE_DUE_DATE_ANCHOR_BEFORE_EVENT,
          dueOffsetDays: remainderDays,
          amountType: QuotePaymentScheduleAmountTypeEnum.QUOTE_PAYMENT_SCHEDULE_AMOUNT_TYPE_PERCENTAGE,
          percentage: 50.0,
        },
      ]
    case 'custom':
    default:
      return []
  }
}

function isFixedQuotePaymentSchedule(schedule: PaymentScheduleFormValue): boolean {
  return schedule.amountType === QuotePaymentScheduleAmountTypeEnum.QUOTE_PAYMENT_SCHEDULE_AMOUNT_TYPE_FIXED
}

function renumberQuotePaymentSchedules(
  schedules: PaymentScheduleFormValue[],
): PaymentScheduleFormValue[] {
  return schedules.map((schedule, index) => ({
    ...schedule,
    sequenceNumber: index + 1,
  }))
}

export function getQuoteFixedPaymentScheduleTotalCents(
  schedules: PaymentScheduleFormValue[],
): number | undefined {
  if (schedules.length === 0 || !schedules.every(isFixedQuotePaymentSchedule)) {
    return undefined
  }

  return schedules.reduce((sum, schedule) => sum + (schedule.amountCents || 0), 0)
}

export function getQuoteFixedPaymentScheduleBalanceDeltaCents(
  schedules: PaymentScheduleFormValue[],
  quoteTotalCents: number,
): number | undefined {
  const scheduleTotalCents = getQuoteFixedPaymentScheduleTotalCents(schedules)
  if (scheduleTotalCents === undefined) return undefined

  return quoteTotalCents - scheduleTotalCents
}

export function rebalanceQuoteFixedPaymentSchedulesForTotal(
  formSchedules: PaymentScheduleFormValue[],
  quoteTotalCents: number,
): PaymentScheduleFormValue[] {
  const currentTotal = getQuoteFixedPaymentScheduleTotalCents(formSchedules)
  if (currentTotal === undefined) return formSchedules

  const delta = quoteTotalCents - currentTotal
  if (delta === 0) return formSchedules

  const next = formSchedules.map((schedule) => ({ ...schedule }))

  if (delta > 0) {
    const lastIndex = next.length - 1
    next[lastIndex] = {
      ...next[lastIndex],
      amountCents: (next[lastIndex].amountCents || 0) + delta,
    }
    return next
  }

  let remainingReduction = -delta

  for (let i = next.length - 1; i >= 0 && remainingReduction > 0; i--) {
    const amountCents = next[i].amountCents || 0

    if (amountCents <= 0) {
      next.splice(i, 1)
      continue
    }

    if (remainingReduction >= amountCents) {
      next.splice(i, 1)
      remainingReduction -= amountCents
      continue
    }

    next[i] = {
      ...next[i],
      amountCents: amountCents - remainingReduction,
    }
    remainingReduction = 0
  }

  if (remainingReduction > 0) return formSchedules

  return renumberQuotePaymentSchedules(next)
}

export function appendQuoteFixedPaymentScheduleForTotal(
  formSchedules: PaymentScheduleFormValue[],
  quoteTotalCents: number,
): PaymentScheduleFormValue[] {
  const currentTotal = getQuoteFixedPaymentScheduleTotalCents(formSchedules)
  if (currentTotal === undefined) return formSchedules

  const delta = quoteTotalCents - currentTotal
  if (delta <= 0) return formSchedules

  const latestDueDate = formSchedules.reduce<Date | undefined>((latest, schedule) => {
    if (!latest || schedule.dueDate > latest) return schedule.dueDate
    return latest
  }, undefined)
  const maxSequenceNumber = formSchedules.reduce((max, schedule) => Math.max(max, schedule.sequenceNumber), 0)

  return [
    ...formSchedules,
    {
      sequenceNumber: maxSequenceNumber + 1,
      description: ADDITIONAL_CHARGES_PAYMENT_SCHEDULE_DESCRIPTION,
      dueDate: latestDueDate ?? new Date(),
      amountType: QuotePaymentScheduleAmountTypeEnum.QUOTE_PAYMENT_SCHEDULE_AMOUNT_TYPE_FIXED,
      amountCents: delta,
    },
  ]
}

// ==================== Invoice Payment Schedule Utilities ====================

/**
 * Serializable form model for invoice payment schedules
 * Simpler than quote schedules - only supports fixed amounts
 */
export interface InvoicePaymentScheduleFormValue {
  id?: string // Set for existing schedules; undefined for newly added ones
  sequenceNumber: number
  description: string
  dueDate: Date
  amountCents: number // Always fixed amount for invoices
}

/**
 * Convert a PaymentSchedule (from API response) to invoice form value
 */
export function invoicePaymentScheduleToFormValue(
  schedule: PaymentSchedule
): InvoicePaymentScheduleFormValue {
  const dueDate = timestampToDateOnly(schedule.dueDate) ?? new Date()

  return {
    id: schedule.id,
    sequenceNumber: schedule.sequenceNumber,
    description: schedule.description,
    dueDate,
    amountCents: schedule.amountCents ? Number(schedule.amountCents) : 0,
  }
}

/**
 * Convert an array of PaymentSchedule[] to invoice form values
 */
export function invoicePaymentSchedulesToFormValues(
  schedules: PaymentSchedule[]
): InvoicePaymentScheduleFormValue[] {
  return schedules.map(invoicePaymentScheduleToFormValue)
}

/**
 * Convert invoice form value to CreatePaymentScheduleInput
 */
export function formValueToInvoicePaymentSchedule(
  formValue: InvoicePaymentScheduleFormValue
): CreatePaymentScheduleInput {
  return create(CreatePaymentScheduleInputSchema, {
    sequenceNumber: formValue.sequenceNumber,
    description: formValue.description,
    dueDate: dateToDateOnlyTimestamp(formValue.dueDate),
    amountCents: BigInt(formValue.amountCents),
  })
}

/**
 * Convert an array of invoice form values to CreatePaymentScheduleInput[]
 */
export function formValuesToInvoicePaymentSchedules(
  formValues: InvoicePaymentScheduleFormValue[]
): CreatePaymentScheduleInput[] {
  return formValues.map(formValueToInvoicePaymentSchedule)
}

function dateKey(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function invoicePaymentScheduleFormSignature(schedule: InvoicePaymentScheduleFormValue): string {
  return [
    schedule.sequenceNumber,
    schedule.description.trim(),
    dateKey(schedule.dueDate),
    schedule.amountCents || 0,
  ].join('|')
}

export function areInvoicePaymentScheduleFormValuesEqual(
  a: InvoicePaymentScheduleFormValue[],
  b: InvoicePaymentScheduleFormValue[],
): boolean {
  if (a.length !== b.length) return false

  const left = a.map(invoicePaymentScheduleFormSignature).sort()
  const right = b.map(invoicePaymentScheduleFormSignature).sort()

  return left.every((signature, index) => signature === right[index])
}

export function hasActiveInvoiceCheckoutSession(schedule: PaymentSchedule, nowMs = Date.now()): boolean {
  const expiresAt = schedule.activeCheckoutSession?.expiresAt
  if (!expiresAt) return false

  const expiresAtMs = Number(expiresAt.seconds) * 1000 + Math.floor(expiresAt.nanos / 1_000_000)
  return expiresAtMs > nowMs
}

export function isInvoicePaymentScheduleLockedForEditing(schedule: PaymentSchedule, nowMs = Date.now()): boolean {
  if (hasActiveInvoiceCheckoutSession(schedule, nowMs)) return true
  if (Number(schedule.amountPaidCents || 0n) > 0) return true

  return schedule.status !== PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PENDING &&
    schedule.status !== PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_OVERDUE
}

export function getInvoicePaymentScheduleLockReason(schedule: PaymentSchedule, nowMs = Date.now()): string | undefined {
  if (hasActiveInvoiceCheckoutSession(schedule, nowMs)) {
    return 'A customer has an active checkout link for this payment.'
  }

  if (
    Number(schedule.amountPaidCents || 0n) > 0 ||
    schedule.status === PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PAID
  ) {
    return 'This payment schedule is locked because a payment has been recorded.'
  }

  if (schedule.status === PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PAYMENT_INITIATED) {
    return 'This payment schedule is locked because payment is being processed.'
  }

  if (isInvoicePaymentScheduleLockedForEditing(schedule, nowMs)) {
    return 'This payment schedule status cannot be edited.'
  }

  return undefined
}

/**
 * A frozen row is one the server preserves on its own during a partial
 * ReplacePaymentSchedules: any row that already has money applied to it.
 * Frozen rows must be left OUT of the replace inputs - the request carries the
 * complete desired set of *editable* rows only.
 */
export function isFrozenInvoicePaymentSchedule(schedule: PaymentSchedule): boolean {
  return Number(schedule.amountPaidCents || 0n) > 0
}

/**
 * The form rows that belong in a ReplacePaymentSchedules request: everything
 * except the rows the server preserves for us.
 */
export function getEditableInvoicePaymentScheduleFormValues(
  existingSchedules: PaymentSchedule[],
  formSchedules: InvoicePaymentScheduleFormValue[],
): InvoicePaymentScheduleFormValue[] {
  const frozenIDs = new Set(
    existingSchedules.filter(isFrozenInvoicePaymentSchedule).map((schedule) => schedule.id),
  )
  return formSchedules.filter((schedule) => !schedule.id || !frozenIDs.has(schedule.id))
}

export interface InvoicePaymentScheduleRowUpdate {
  id: string
  description: string
  dueDate: Date
  /** UpdatePaymentSchedule field-mask paths ('description' and/or 'due_date') */
  paths: string[]
}

export type InvoicePaymentScheduleSyncPlan =
  | { kind: 'replace'; schedules: InvoicePaymentScheduleFormValue[] }
  | { kind: 'row-updates'; updates: InvoicePaymentScheduleRowUpdate[] }

function invoicePaymentScheduleDueDate(schedule: PaymentSchedule): Date {
  return timestampToDateOnly(schedule.dueDate) ?? new Date()
}

/**
 * Decides how to persist an edited invoice payment schedule.
 *
 * - No frozen rows: full replace, exactly as before (every row is sent).
 * - Frozen rows present and an editable amount (or the editable row set)
 *   changed: replace carrying ONLY the editable rows - the server re-attaches
 *   the frozen rows and validates sum(inputs) + sum(frozen) == invoice total.
 * - Frozen rows present and only description/due-date changed: the cheap
 *   per-row UpdatePaymentSchedule path, which leaves amounts untouched.
 */
export function planInvoicePaymentScheduleSync(
  existingSchedules: PaymentSchedule[],
  formSchedules: InvoicePaymentScheduleFormValue[],
  nowMs = Date.now(),
): InvoicePaymentScheduleSyncPlan {
  if (!existingSchedules.some(isFrozenInvoicePaymentSchedule)) {
    return { kind: 'replace', schedules: formSchedules }
  }

  const existingByID = new Map(existingSchedules.map((schedule) => [schedule.id, schedule]))
  const editableFormSchedules = getEditableInvoicePaymentScheduleFormValues(existingSchedules, formSchedules)

  const amountChanged = editableFormSchedules.some((formSchedule) => {
    const existing = formSchedule.id ? existingByID.get(formSchedule.id) : undefined
    if (!existing) return true
    return Number(existing.amountCents || 0n) !== (formSchedule.amountCents || 0)
  })
  const editableRowRemoved = existingSchedules.some((schedule) =>
    !isFrozenInvoicePaymentSchedule(schedule) &&
    !formSchedules.some((formSchedule) => formSchedule.id === schedule.id),
  )

  if (amountChanged || editableRowRemoved) {
    return { kind: 'replace', schedules: editableFormSchedules }
  }

  const updates: InvoicePaymentScheduleRowUpdate[] = []
  for (const formSchedule of formSchedules) {
    if (!formSchedule.id) continue

    const existing = existingByID.get(formSchedule.id)
    if (!existing || getInvoicePaymentScheduleLockReason(existing, nowMs)) continue

    const paths: string[] = []
    if ((existing.description || '') !== (formSchedule.description || '')) {
      paths.push('description')
    }
    if (dateKey(invoicePaymentScheduleDueDate(existing)) !== dateKey(formSchedule.dueDate)) {
      paths.push('due_date')
    }
    if (paths.length === 0) continue

    updates.push({
      id: formSchedule.id,
      description: formSchedule.description,
      dueDate: formSchedule.dueDate,
      paths,
    })
  }

  return { kind: 'row-updates', updates }
}

function isFormScheduleLocked(
  schedule: InvoicePaymentScheduleFormValue,
  existingByID: Map<string, PaymentSchedule>,
): boolean {
  if (!schedule.id) return false

  const existing = existingByID.get(schedule.id)
  if (!existing) return false

  return isInvoicePaymentScheduleLockedForEditing(existing)
}

function isFormScheduleExcludedFromLastPaymentRebalance(
  schedule: InvoicePaymentScheduleFormValue,
  existingByID: Map<string, PaymentSchedule>,
): boolean {
  if (!schedule.id) return false

  const existing = existingByID.get(schedule.id)
  if (!existing) return false

  if (Number(existing.amountPaidCents || 0n) > 0) return true

  return existing.status !== PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PENDING &&
    existing.status !== PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_OVERDUE
}

function hasFormScheduleActiveCheckoutSession(
  schedule: InvoicePaymentScheduleFormValue,
  existingByID: Map<string, PaymentSchedule>,
): boolean {
  if (!schedule.id) return false

  const existing = existingByID.get(schedule.id)
  return Boolean(existing && hasActiveInvoiceCheckoutSession(existing))
}

export function rebalanceInvoicePaymentSchedulesForTotal(
  existingSchedules: PaymentSchedule[],
  formSchedules: InvoicePaymentScheduleFormValue[],
  invoiceTotalCents: number,
): InvoicePaymentScheduleFormValue[] {
  if (formSchedules.length === 0) return formSchedules

  const existingByID = new Map(existingSchedules.map((schedule) => [schedule.id, schedule]))
  const next = formSchedules.map((schedule) => ({ ...schedule }))
  const currentTotal = next.reduce((sum, schedule) => sum + (schedule.amountCents || 0), 0)
  const delta = invoiceTotalCents - currentTotal

  if (delta === 0) return next

  const lockedTotal = next.reduce((sum, schedule) => {
    return isFormScheduleLocked(schedule, existingByID) ? sum + (schedule.amountCents || 0) : sum
  }, 0)

  if (invoiceTotalCents < lockedTotal) return formSchedules

  const editableIndexes = next
    .map((schedule, index) => ({ schedule, index }))
    .filter(({ schedule }) => !isFormScheduleLocked(schedule, existingByID))
    .map(({ index }) => index)

  const appendAdditionalSchedule = (): InvoicePaymentScheduleFormValue[] => {
    const latestDueDate = next.reduce<Date | undefined>((latest, schedule) => {
      if (!latest || schedule.dueDate > latest) return schedule.dueDate
      return latest
    }, undefined)
    const maxSequenceNumber = next.reduce((max, schedule) => Math.max(max, schedule.sequenceNumber), 0)

    return [
      ...next,
      {
        sequenceNumber: maxSequenceNumber + 1,
        description: ADDITIONAL_CHARGES_PAYMENT_SCHEDULE_DESCRIPTION,
        dueDate: latestDueDate ?? new Date(),
        amountCents: delta,
      },
    ]
  }

  if (delta > 0) {
    const lastEditableIndex = next
      .map((schedule, index) => ({ schedule, index }))
      .filter(({ schedule }) => !isFormScheduleExcludedFromLastPaymentRebalance(schedule, existingByID))
      .map(({ index }) => index)
      .at(-1)

    if (lastEditableIndex !== undefined) {
      if (hasFormScheduleActiveCheckoutSession(next[lastEditableIndex], existingByID)) {
        return appendAdditionalSchedule()
      }

      next[lastEditableIndex] = {
        ...next[lastEditableIndex],
        amountCents: (next[lastEditableIndex].amountCents || 0) + delta,
      }
      return next
    }

    return appendAdditionalSchedule()
  }

  let remainingReduction = -delta

  for (let i = editableIndexes.length - 1; i >= 0 && remainingReduction > 0; i--) {
    const index = editableIndexes[i]
    const amountCents = next[index].amountCents || 0

    if (amountCents <= 0) {
      next.splice(index, 1)
      continue
    }

    if (remainingReduction >= amountCents) {
      next.splice(index, 1)
      remainingReduction -= amountCents
      continue
    }

    next[index] = {
      ...next[index],
      amountCents: amountCents - remainingReduction,
    }
    remainingReduction = 0
  }

  if (remainingReduction > 0) return formSchedules

  return next
}

export function appendInvoicePaymentScheduleForTotal(
  formSchedules: InvoicePaymentScheduleFormValue[],
  invoiceTotalCents: number,
): InvoicePaymentScheduleFormValue[] {
  if (formSchedules.length === 0) return formSchedules

  const currentTotal = formSchedules.reduce((sum, schedule) => sum + (schedule.amountCents || 0), 0)
  const delta = invoiceTotalCents - currentTotal
  if (delta <= 0) return formSchedules

  const latestDueDate = formSchedules.reduce<Date | undefined>((latest, schedule) => {
    if (!latest || schedule.dueDate > latest) return schedule.dueDate
    return latest
  }, undefined)
  const maxSequenceNumber = formSchedules.reduce((max, schedule) => Math.max(max, schedule.sequenceNumber), 0)

  return [
    ...formSchedules,
    {
      sequenceNumber: maxSequenceNumber + 1,
      description: ADDITIONAL_CHARGES_PAYMENT_SCHEDULE_DESCRIPTION,
      dueDate: latestDueDate ?? new Date(),
      amountCents: delta,
    },
  ]
}
